import { MessageResponse } from './ai-elements/message';
import { DataPartHelper } from './data-part-helper';
import { DataRoomUploadPill } from './data-room/DataRoomUploadPill';

/** Longest prose field rendered as the body; anything shorter is a label rather than content. */
const PROSE_MIN_LENGTH = 80;

/** `data-research-finding` → `Research finding`. The part type is the only name a generic data part
 * carries, so it is what titles the block. */
function titleFor(partType: string): string {
  const words = partType
    .replace(/^data-/, '')
    .split('-')
    .filter(Boolean);
  const [first, ...rest] = words;

  if (!first) return 'Details';

  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

function readableEntries(data: unknown): [string, string][] {
  if (typeof data !== 'object' || data === null || Array.isArray(data))
    return [];

  return Object.entries(data as Record<string, unknown>).flatMap(
    ([key, value]): [string, string][] => {
      if (typeof value === 'string' && value.trim()) return [[key, value]];
      if (typeof value === 'number' || typeof value === 'boolean')
        return [[key, String(value)]];

      return [];
    }
  );
}

/**
 * The renderer of last resort for a `data-*` part nothing else claims.
 *
 * Research alone contributes eight such types and the builder two more, and the grouper used to
 * drop every one of them silently, so a whole research answer drew as an empty turn. Showing the
 * part's own readable fields is worse-looking than a purpose-built card and far better than
 * nothing, and it means a new data part added later can never vanish without trace.
 */
export function DataPartBlock({
  partType,
  data,
}: {
  partType: string;
  data: unknown;
}) {
  // A malformed data-room part falls through to the generic block rather than vanishing.
  const dataRoom =
    DataPartHelper.getKind(partType) === 'document-upload'
      ? DataPartHelper.getDocumentUpload(data)
      : null;
  if (dataRoom) return <DataRoomUploadPill dataRoom={dataRoom} />;

  const entries = readableEntries(data);

  if (entries.length === 0) return null;

  const proseEntry = entries.find(
    ([, value]) => value.length >= PROSE_MIN_LENGTH
  );
  const labels = entries.filter((entry) => entry !== proseEntry);

  return (
    <div className="border-chat-border mt-md rounded-lg border p-md">
      <p className="txt-extra-small-medium text-text-tertiary mb-sm">
        {titleFor(partType)}
      </p>
      {proseEntry ? <MessageResponse>{proseEntry[1]}</MessageResponse> : null}
      {labels.length > 0 && (
        <dl className="gap-x-md mt-sm grid grid-cols-[auto_1fr] gap-y-xs">
          {labels.map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="txt-extra-small-regular text-text-quaternary">
                {key}
              </dt>
              <dd className="txt-extra-small-regular text-text-secondary break-words">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
